import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { formatSuffixValue } from "../util";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useGaiaObjectCustomAttributes } from "hooks/useGaiaObjectCustomAttributes";
import { transformCustomAttributesDataMap } from "utils/dynamicAttributesUtils";

/**
 * Custom hook for form initialization logic
 * Handles form reset and initialization effects using adapter pattern
 */
export const useFormInit = (
  shortId: string | undefined,
  reset: any,
  setValue: any,
  formData: any,
  adapter: any,
  mode: string
) => {
  const {
    uiContent,
    defaultCountry,
    scanJobData,
    scanResultData,
    customAttributes,
    transformedGaiaObject,
    currentUser,
  } = formData;

  const localStorageKey = `notice-draft-wizard-step1-${shortId}`;
  const [localFormData, setLocalFormData] = useLocalStorage<any>(
    localStorageKey,
    null
  );

  useEffect(() => {
    adapter.initializeFormData({
      reset,
      setValue,
      defaultCountry,
      transformedGaiaObject,
      scanResultData,
      scanJobData,
      customAttributes,
      uiContent,
      currentUser,
      localFormData,
      shortId,
    });
  }, [
    mode,
    transformedGaiaObject?.gaiaObjectId,
    scanResultData,
    scanJobData,
    customAttributes,
    uiContent,
    defaultCountry,
    localFormData,
    shortId,
    reset,
    setValue,
    adapter,
  ]);

  return {
    localFormData,
    setLocalFormData,
  };
};

/**
 * Custom hook for form validation logic
 */
export const useFormValidation = (
  formSchema: any,
  defaultCountry: string,
  defaultStatus: string,
  adapter: any
) => {
  const { handleSubmit, control, formState, watch, reset, setValue } =
    useForm<any>({
      mode: "onChange",
      resolver: zodResolver(formSchema),
      defaultValues: adapter.getDefaultFormValues(
        defaultCountry,
        defaultStatus
      ),
    });

  return {
    handleSubmit,
    control,
    formState,
    watch,
    reset,
    setValue,
  };
};

/**
 * Custom hook for form field effects logic
 */
export const useSuffixFieldEffects = (
  uiContent: any,
  watch: any,
  setValue: any
) => {
  const suffixSourceField = uiContent?.suffixField
    ? watch(`${CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}.${uiContent.suffixField}`)
    : watch(`${uiContent?.suffixField}`);

  const titleSuffix = watch("titleSuffix");
  const prevSuffixSourceFieldRef = useRef<any>(undefined);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (uiContent?.suffixField && suffixSourceField) {
      if (prevSuffixSourceFieldRef.current !== suffixSourceField) {
        const formattedValue = formatSuffixValue(
          suffixSourceField,
          uiContent.suffixField
        );

        if (hasInitializedRef.current || !titleSuffix) {
          setValue("titleSuffix", formattedValue, { shouldValidate: true });
        }

        prevSuffixSourceFieldRef.current = suffixSourceField;
        hasInitializedRef.current = true;
      }
    }
  }, [suffixSourceField, uiContent?.suffixField, setValue, titleSuffix]);

  return {
    titleSuffix,
  };
};

/**
 * Custom hook for wizard integration logic
 */
export const useWizardIntegration = (
  adapter: any,
  shortId: string | undefined,
  triggerWizardValidation = 0,
  handleSubmit: any,
  onValid: any,
  onInvalid: any,
  watch: any
) => {
  const prevTriggerWizardValidationRef = useRef(triggerWizardValidation);

  useEffect(() => {
    if (
      triggerWizardValidation > 0 &&
      triggerWizardValidation > prevTriggerWizardValidationRef.current
    ) {
      handleSubmit(onValid, onInvalid)();
    }

    prevTriggerWizardValidationRef.current = triggerWizardValidation;
  }, [triggerWizardValidation, handleSubmit, onValid, onInvalid]);

  const getWizardImperativeHandleMethods = (
    localFormData: any,
    setLocalFormData: any
  ) => ({
    getFormData: () => {
      const formData = watch ? watch() : undefined;
      if (adapter.shouldPersistFormData(shortId) && formData) {
        setLocalFormData(formData);
      }
      return formData;
    },
    submitForm: () => {
      handleSubmit(onValid, onInvalid)();
    },
    validateForm: () => {
      return handleSubmit(onValid, onInvalid)();
    },
  });

  return {
    getWizardImperativeHandleMethods,
  };
};

/** Retrieves custom attributes, schemas, and transforms Gaia object data for form usage */
export const useTransformedGaiaObject = (
  type: string,
  gaiaObjectData: any,
  isLoadingGaiaObjectData?: boolean
) => {
  const { customAttributes, createSchema, formSchema } =
    useGaiaObjectCustomAttributes(
      type,
      gaiaObjectData,
      isLoadingGaiaObjectData
    );

  const transformedGaiaObject = useMemo(() => {
    if (!customAttributes || !gaiaObjectData?.gaiaObject) return null;
    return transformCustomAttributesDataMap(
      gaiaObjectData.gaiaObject,
      customAttributes!,
      "YYYY-MM-DD"
    );
  }, [customAttributes, gaiaObjectData]);

  return { customAttributes, createSchema, formSchema, transformedGaiaObject };
};
